/**
 * Auth boutique B2C — Contexte React INDÉPENDANT de `lib/auth.js` (admin).
 *
 * Stocke l'état du client connecté (`customer`), gère login/logout/register,
 * et appelle automatiquement `/api/shop/auth/me` au montage pour restaurer la session.
 *
 * États :
 *   - customer = null  → vérification en cours
 *   - customer = false → non authentifié
 *   - customer = {id, email, name, ...} → authentifié
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOP_API = `${BACKEND_URL}/api/shop/auth`;

// Instance axios dédiée — withCredentials pour envoyer les cookies shop_*
export const shopApi = axios.create({
  baseURL: `${BACKEND_URL}/api/shop`,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Phase 35 — i18n des emails Resend. Le frontend transmet sa langue active
// via le header X-Lang à chaque requête boutique afin que le backend envoie
// les emails (vérification / reset password / confirmation commande) dans
// la bonne langue.
shopApi.interceptors.request.use((config) => {
  try {
    const lang = (typeof window !== "undefined"
      ? window.localStorage.getItem("maison-thai-lang")
      : null) || "FR";
    config.headers = config.headers || {};
    config.headers["X-Lang"] = lang;
  } catch {
    /* noop */
  }
  return config;
});

// Phase 87r (15/06/2026) — Refresh automatique transparent sur 401.
// Bug signalé : une cliente Chrome se retrouvait déconnectée au moment du
// checkout (POST /orders renvoyait 401 car access_token expiré, et la page
// la redirigeait brutalement vers /login). Or `shop_refresh_token` reste
// valide 30 jours et `/auth/refresh` accepte le cookie pour ré-émettre un
// access fresh. On intercepte donc tous les 401, on appelle /auth/refresh
// (UNE FOIS pour éviter les loops), puis on rejoue la requête originale.
let _shopRefreshInflight = null;
shopApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    // Conditions :
    //   - 401 (Non authentifié / Session expirée / Jeton invalide)
    //   - on n'a pas déjà retry (évite boucle infinie)
    //   - l'URL n'est PAS /auth/refresh elle-même (sinon loop garantie)
    //   - l'URL n'est PAS /auth/login (un 401 sur login = mauvais mdp, pas un refresh)
    if (
      status === 401 &&
      original &&
      !original._shopRetried &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login")
    ) {
      original._shopRetried = true;
      try {
        // Mutualise les refresh concurrents (si plusieurs requêtes échouent en parallèle)
        if (!_shopRefreshInflight) {
          _shopRefreshInflight = shopApi
            .post("/auth/refresh")
            .finally(() => { _shopRefreshInflight = null; });
        }
        await _shopRefreshInflight;
        // Rejoue la requête originale (le nouveau cookie est déjà set par le backend)
        return shopApi.request(original);
      } catch {
        // Refresh échoué (refresh_token expiré ou invalide) → laisse remonter le 401
      }
    }
    return Promise.reject(error);
  },
);

const ShopAuthContext = createContext(null);

export function ShopAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [reseller, setReseller] = useState(null); // Phase 31 : revendeur lié au client connecté

  const checkSession = useCallback(async () => {
    try {
      const { data } = await shopApi.get("/auth/me");
      setCustomer(data.customer);
      setReseller(data.reseller || null);
    } catch {
      setCustomer(false);
      setReseller(null);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await shopApi.post("/auth/login", { email, password });
    setCustomer(data.customer);
    // L'endpoint /login ne renvoie pas le reseller — on re-checke la session
    checkSession();
    return data.customer;
  };

  const register = async (payload) => {
    const { data } = await shopApi.post("/auth/register", payload);
    return data; // {ok, customer (non connecté), message}
  };

  const logout = async () => {
    try { await shopApi.post("/auth/logout"); } catch { /* noop */ }
    setCustomer(false);
    setReseller(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await shopApi.put("/auth/me", patch);
    setCustomer(data.customer);
    return data.customer;
  };

  return (
    <ShopAuthContext.Provider value={{
      customer, reseller, login, register, logout, updateProfile,
      refresh: checkSession,
    }}>
      {children}
    </ShopAuthContext.Provider>
  );
}

export const useShopAuth = () => useContext(ShopAuthContext);

/** Format un detail FastAPI (string ou array) en string affichable. */
export function formatShopAuthError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
